import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type UserMini = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  position?: string;
  org?: string;
  createdAt?: string;
  updatedAt?: string;
  completedWelcome?: boolean;
  isDemo?: boolean;
  __v?: number;
  password?: string;
};

export type Attendee = {
  _id: string;
  userType?: string;
  status?: string;
  user: UserMini;
};

export type RecurringPattern = {
  daysOfWeek: string[];
};

export type ProjectRef = {
  _id: string;
  name?: string;
};

export type Reminder = {
  _id?: string;
  type?: string;
  time?: number;
  sent?: boolean;
};

export type Meeting = {
  _id: string;
  id?: string;
  title?: string;
  description?: string;
  organizer?: { _id: string; email?: string };
  attendees?: Attendee[];
  scheduledDate?: string;
  duration?: number;
  status?: string;
  meetingPlatform?: string;
  meetingLink?: string;
  projectId?: ProjectRef | string | null;
  organizationId?: string;
  agenda?: string;
  isRecurring?: boolean;
  recurringPattern?: RecurringPattern;
  reminders?: Reminder[];
  attachments?: any[];
  createdAt?: string;
  updatedAt?: string;
  meetingType?: string;
  endTime?: string;
  __v?: number;
};

export type MeetingsResponse = {
  success: boolean;
  data: {
    meetings: Meeting[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalMeetings: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
  message?: string;
};

const fetchMeetings = async (
  params?: Record<string, string | number | boolean | undefined>
): Promise<MeetingsResponse> =>
  (await customAxios.get<MeetingsResponse>("/meetings", { params })).data;

export const useGetMeetings = (
  params?: Record<string, string | number | boolean | undefined>
) =>
  useQuery<MeetingsResponse, AxiosError>(
    ["meetings", params ?? {}],
    () => fetchMeetings(params),
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Meetings",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to load meetings",
          color: "red",
        }),
      staleTime: 0,
      retry: 1,
      refetchOnMount: "always",
    }
  );
